import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {DataFormatService} from "../../../services/data-format.service";
import ProjectStatus from "../../../interfaces/project-status";
import ProjectDdChecklistGroup from "../../../interfaces/project-dd-checklist-group";
import {PipelineProjectDate} from "../../../interfaces/pipeline-project-date";
import {PipelineProject} from "../../../interfaces/pipeline-project";
import Participant from "../../../interfaces/participant";
import Sponsor from "../../../interfaces/sponsor";
import {DueDiligenceService} from "../../due-diligence.service";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import ProjectDdChecklistSelection from "../../../interfaces/project-dd-checklist-selection";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {
  DueDiligenceAddDateModalComponent
} from "../due-diligence-add-date-modal/due-diligence-add-date-modal.component";
import {DatePipe} from "@angular/common";
import Permission from "../../../interfaces/permission";
import {MatSnackBar, MatSnackBarConfig} from "@angular/material/snack-bar";


export enum DdItemInfoDateCategory {
  LOI_SIGNED = 'loi_signed_date',
  CAPITAL = 'capital_date',
  CLOSED = 'closed_date',
  SCREENING = 'screening_date'
}
interface PastDate {
  label: string;
  date: string;
}

export interface DiligenceItem {
  name: string | null;
  dealUrl: string | null;
  status: string | null;
  lt_equity: number | null;
  noi: number | null
  fund: string | null
  fundUrl: string | null
  sponsor: string | null
  sponsorUrl: string | null
  acc_manager: string | null
  acc_managerUrl: string | null
  closing_coordinator: string | null
  closing_coordinatorUrl: string | null
  underwriter: string | null
  underwriterUrl: string | null
  uw_analyst: string | null
  uw_analystUrl: string | null
  construction_analyst: string | null
  construction_analystUrl: string | null
  loi_signed_date: string | null
  screening_date: string | null
  capital_date: string | null
  closed_date: string | null
  sharepointUrl: string | null
  address: string | null
  timing_issues: string | null
  screening_delay: number | 0
  high_priority: boolean
}

interface AdditionalDate {
  label: string;
  date: string;
}

export interface TrackerItem {
  name: string;
  date?: string;
  status: 'approved' | 'received' | 'outstanding' | 'none';
  selectedStatus: string;
  pastDates: PastDate[];
  additionalDates: AdditionalDate[];
  expanded: boolean;
}

export interface Panel {
  title: string;
  items: TrackerItem[];
}

@Component({
  selector: 'due-diligence-item',
  templateUrl: './due-diligence-item.component.html',
  styleUrls: ['./due-diligence-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DueDiligenceItemComponent implements OnInit{
  @Input() item: ProjectDdChecklistGroup
  @Input() checklists: Panel[]
  public diligenceItem:DiligenceItem;

  public selectionMap: Map<string, ProjectDdChecklistSelection> = new Map()

  public itemForms: Map<string, FormGroup> = new Map();
  public statuses:any[] = []


  dates = [
    { label: 'LOI Signed', date: '', alert: false },
    { label: 'Market Study Expired', date: '', alert: false },
    { label: 'Screening Committee', date: '', alert: false },
    { label: 'Capital Committee', date: '', alert: false },
    { label: 'Closed', date: '', alert: false }
  ];

  public timingDelay: FormControl = new FormControl();
  public highPriority: FormControl = new FormControl(false);
  public defaultStatus: any;

  public timingFormControl: FormControl = new FormControl()

  constructor(public ddService: DueDiligenceService, private dialog: MatDialog, private datePipe: DatePipe, public _snackbar: MatSnackBar, public cd: ChangeDetectorRef) {
  }
  ngOnInit() {
    this.diligenceItem = this.ddService._formatProjectChecklistGroups(this.item)
    let selections = this.ddService.setSelections(this.item.projectDdChecklistSelections)
    this.ddService.statuses$.subscribe((statuses:any) => {
      this.statuses = statuses
      statuses.forEach((status:any) => {
        if(status.name=='Outstanding'){
          this.defaultStatus = status
        }
      })
    })

    selections.forEach((sel: ProjectDdChecklistSelection) => {
      if(sel.ddChecklistItem?.id){
        this.selectionMap.set(sel.ddChecklistItem.id, sel)
      }
    })

    this.ddService.checklists$.subscribe((panels: any[]) => {
      panels.forEach((panel:any) => {
        panel.ddChecklistItems.forEach((checklistItem:any) => {
          this.buildItemForm(checklistItem)
        })
      })
    })
    if(this.diligenceItem.timing_issues!==null){
      this.timingFormControl.setValue(this.diligenceItem.timing_issues)
    }
    this.highPriority.setValue(this.diligenceItem.high_priority ?? false)
    this.timingDelay.setValue(this.diligenceItem.screening_delay ?? 0)
    this.setDates(this.diligenceItem)

  }

  private buildItemForm(checklistItem:any){
    const selection = this.getSelectionForItem(checklistItem)
    const pastDates = selection?.projectDdChecklistDates ?? []
    const allDates = this.ddService['_dates'] ?? []
    const detailDates = selection?.projectDdChecklistDetailDates ?? []

    const pastDateControls = pastDates.map((pd:any) => {
      let split = pd.date.split('-')
      return new FormGroup({
        id: new FormControl(pd.id),
        statusId: new FormControl(pd.ddChecklistState?.id),
        statusName: new FormControl(pd.ddChecklistState?.name),
        date: new FormControl(new Date(split[0], split[1]-1, split[2]))
      })
    })

    const additionalDateControls = allDates.map((d:any) => {
      const existingDate = this.getDetailDateForSelectionItem(detailDates, d.name)
      let split = []
      if(existingDate){
        split = existingDate.date.split('-')
      }
      return new FormGroup({
        ddChecklistDateId: new FormControl(d.id),
        name: new FormControl(d.name),
        date: new FormControl(existingDate? new Date(split[0], split[1]-1, split[2]) : ''),
        id: new FormControl(existingDate?.id)
      })
    })

    const form = new FormGroup({
      status: new FormControl(selection?.ddChecklistState.id || this.defaultStatus.id),
      pastDates: new FormArray(pastDateControls),
      additionalDates: new FormArray(additionalDateControls)
    })

    this.itemForms.set(checklistItem.id, form)
  }

  getItemForm(checklistItem: any): FormGroup | undefined {
    return this.itemForms.get(checklistItem.id);
  }

  getPastDatesArray(checklistItem: any): FormArray {
    return this.getItemForm(checklistItem)?.get('pastDates') as FormArray;
  }

  getAdditionalDatesArray(checklistItem: any): FormArray {
    return this.getItemForm(checklistItem)?.get('additionalDates') as FormArray;
  }

  setDates(item: DiligenceItem){
    let marketItem = Array.from(this.selectionMap.values())?.find((sel:any) => sel.ddChecklistItem.name=='Market Study')
    for(let date of this.dates){
      switch (date.label) {
        case 'Screening Committee':
          date.date = item.screening_date ?? 'TBD'
          break;
        case 'Capital Committee':
          date.date = item.capital_date ?? 'TBD'
          break;
        case 'Closed':
          date.date = item.closed_date ?? 'TBD'
          break;
        case 'LOI Signed':
          date.date = item.loi_signed_date ?? 'TBD'
          break;
        case 'Market Study Expired':
          let expirDate = marketItem ? marketItem.projectDdChecklistDetailDates.find((date:any) => date.ddChecklistDate.name.toLowerCase()=='expiration') : null
          date.date = expirDate ? expirDate.date : 'TBD'
          if(date.date!==null && date.date!=='TBD'){
            date.alert = new Date() > new Date(date.date)
          }
          break;
        default: date.date = 'TBD'
      }
    }
  }


  openAddDateModal(item:any){
    let ref: MatDialogRef<DueDiligenceAddDateModalComponent> = this.dialog.open(DueDiligenceAddDateModalComponent, {
      width: '600px',
      height: '300px',
    })
    let instance = ref.componentInstance.onSave.subscribe((data:any) => {
      let dataVar:any = {
        id: this.getSelectionForItem(item)?.id,
        dd_checklist_state_id: data.status.id,
        date: this.datePipe.transform(new Date(data.date), 'yyyy-MM-dd')
      }
      this.ddService.addProjectDueDiligenceDate(dataVar.dd_checklist_state_id, dataVar.id, dataVar.date).subscribe((response:any) => {
        let newDateData = response.data?.addProjectDdChecklistDate
        let split = newDateData.date.split('-')
        let newPastDateControl = new FormGroup({
          id: new FormControl(newDateData.id),
          statusId: new FormControl(newDateData.ddChecklistState?.id),
          statusName: new FormControl(newDateData.ddChecklistState?.name),
          date: new FormControl(new Date(split[0], split[1]-1, split[2]))
        })
        let form = this.getItemForm(item)
        let pastDatesArray = form?.get('pastDates') as FormArray
        pastDatesArray.push(newPastDateControl)
        this.cd.markForCheck()

        this.openSnackBar('Date added.', 'save-success')
      })
    })
  }

  getSelectionForItem(ddChecklistItem:any) {
    return this.selectionMap.get(ddChecklistItem.id) ?? null
  }

  getDateForSelectionItem(dates: any[]){
    const recentDate = dates.reduce((latest:any, current:any) =>
    new Date(current.date)> new Date(latest.date) ? current : latest,
    dates[0])
    return recentDate ?? null
  }

  getDetailDateForSelectionItem(detailDates: any[], name: string){
    let date = detailDates.find((dDate:any) => dDate.ddChecklistDate.name.toLowerCase() == name.toLowerCase())
    return date ?? null
  }

  selectionSave(item:ProjectDdChecklistSelection | null, checklist: any){
    const form = this.getItemForm(checklist)
    if(!form){
      return;
    }
    const selectedStatus = form.get('status')?.value
    const pastDates = (form.get('pastDates') as FormArray).controls
    const additionalDates = (form.get('additionalDates') as FormArray).controls

    const formatDate = (val:any) => this.datePipe.transform(new Date(val), 'yyyy-MM-dd') ?? ''

    if(item==null){
      this.ddService.addProjectDdChecklistSelection(this.item.id, checklist.id, selectedStatus, formatDate(new Date())).subscribe((response:any) => {
        let data:any = response.data.addProjectDdChecklistSelection
        pastDates.forEach((ctrl, index) => {
          const pd = ctrl.value;
          if (pd.id && pd.date) {
            this.ddService.updateProjectDueDiligenceDate(pd.id, formatDate(pd.date)).subscribe({
              error: (error) => this.openSnackBar('Error saving checklist', 'save-fail'),

              next: (result:any) => {

              }

            });
          } else {
            this.ddService.addProjectDueDiligenceDate(pd.statusId, data.id,formatDate(pd.date)).subscribe((result) => {

            })
          }
        });
        let savedCount = 0
        additionalDates.forEach((ctrl) => {
          const ad = ctrl.value;
          if (ad.date) {
            if(ad.id!==null){
              this.ddService.updateProjectDueDiligenceDetailDate(ad?.id, formatDate(ad.date)).subscribe({
                error: (error) => this.openSnackBar('Error saving checklist', 'save-fail'),
                next: (result) => {
                  savedCount += 1
                  if(savedCount===additionalDates.length){
                    this.openSnackBar('Saved.', 'save-success')
                  }
                }
              });

            } else {
              this.ddService.addProjectDueDiligenceDetailDate(data.id ?? '', ad.ddChecklistDateId, formatDate(ad.date)).subscribe({
                error: (error) => this.openSnackBar('Error saving checklist', 'save-fail'),
                next: (result) => {
                  savedCount += 1
                  if(savedCount===additionalDates.length){
                    this.openSnackBar('Saved.', 'save-success')
                  }
                }
              })
            }
          } else {
            savedCount += 1
            if(savedCount===additionalDates.length){
              this.openSnackBar('Saved.', 'save-success')
            }
          }
        });
      })
    } else {
      this.ddService.updateProjectDdChecklistSelection(item?.id ?? '', selectedStatus).subscribe({
        error: (error) => this.openSnackBar('Error saving checklist', 'save-fail'),
        next: (result:any) => {

        }
      });


      pastDates.forEach((ctrl, index) => {
        const pd = ctrl.value;
        if (pd.id && pd.date) {
          this.ddService.updateProjectDueDiligenceDate(pd.id, formatDate(pd.date)).subscribe({
            error: (error) => this.openSnackBar('Error saving checklist', 'save-fail'),

            next: (result:any) => {
              // let resultDate:any = result.data.updateProjectDdChecklistDate;
              //
              // (form.get('pastDates') as FormArray).at(index).patchValue({
              //   id: resultDate.id,
              //   date: new Date(resultDate.date),
              //   statusName: resultDate.ddChecklistState.name,
              //   statusId: resultDate.ddChecklistState.id
              // });
            }

          });
        } else {

          this.ddService.addProjectDueDiligenceDate(pd.statusId, item?.id ?? '',formatDate(pd.date)).subscribe((result) => {
          })
        }
      });

      let savedCount = 0
      additionalDates.forEach((ctrl) => {
        const ad = ctrl.value;
        if (ad.date) {
          if(ad.id!==null){
            this.ddService.updateProjectDueDiligenceDetailDate(ad?.id, formatDate(ad.date)).subscribe({
              error: (error) => this.openSnackBar('Error saving checklist', 'save-fail'),
              next: (result) => {
                savedCount += 1
                if(savedCount===additionalDates.length){
                  this.openSnackBar('Saved.', 'save-success')
                }
              }
            });

          } else {
            this.ddService.addProjectDueDiligenceDetailDate(item?.id ?? '', ad.ddChecklistDateId, formatDate(ad.date)).subscribe({
              error: (error) => this.openSnackBar('Error saving checklist', 'save-fail'),
              next: (result) => {
                savedCount += 1
                if(savedCount===additionalDates.length){
                  this.openSnackBar('Saved.', 'save-success')
                }
              }
            })
          }
        } else {
          savedCount += 1
          if(savedCount===additionalDates.length){
            this.openSnackBar('Saved.', 'save-success')
          }
        }
      });
      //this.ddService.getProjectDdChecklistGroup(this.item.id, () => this.openSnackBar('Save successful', 'save-success'))
    }
  }

  updateProjectDdGroup(){
    this.ddService.updateProjectDdGroup(this.item.id, this.timingFormControl.value, this.highPriority.value, this.timingDelay.value ?? 0).
      subscribe((response:any) => {
        this.ddService.getProjectDdChecklistGroup(this.item.id, () => this.openSnackBar('Save successful', 'save-success'))
    })
  }

  changeStatus(item:any, event:any){
    let form = this.getItemForm(item)
    form?.patchValue({
      status: event.value
    })

    const selection = this.selectionMap.get(item.id);
    let status = this.statuses.find((status:any) => status.id === event.value)
    const hasStatus:any = selection?.projectDdChecklistDates?.find((date:any) => date?.ddChecklistState?.name == status.name)
    let newPastDateControl = new FormGroup({
      id: new FormControl(),
      statusId: new FormControl(event.value),
      statusName: new FormControl(status?.name),
      date: new FormControl(new Date())
    })
    let pastDatesArray = form?.get('pastDates') as FormArray
    if(!hasStatus){
      pastDatesArray.push(newPastDateControl)
      if (!selection) {
        return
      }
      const updatedSelection = {
        ...selection,
        ddChecklistState: {
          ...selection.ddChecklistState,
          name: status?.name
        }
      };
      this.selectionMap.set(item.id, updatedSelection);
    } else {
      const index = pastDatesArray.controls.findIndex(
        (ctrl) => ctrl.get('statusId')?.value === hasStatus.ddChecklistState?.id
      );
      const replacedDate = pastDatesArray.controls[index]
      newPastDateControl.controls['id'].setValue(replacedDate.get('id')?.value)
      if (index !== -1) {
        pastDatesArray.setControl(index, newPastDateControl);
      }
      const newDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd') ?? '';
      let updatedProjectDates = selection?.projectDdChecklistDates.map((date:any) => date.ddChecklistState.name === hasStatus.name ? {...date, date:newDate} : date)
      if(!selection){
        return
      }
      let updatedSelection = {
        ...selection,
        ddChecklistState: {
          ...selection?.ddChecklistState,
          name: status?.name
        },
        projectDdChecklistDates: updatedProjectDates ? [...updatedProjectDates] : []
      }
      this.selectionMap.set(item.id, updatedSelection);
    }

    this.cd.markForCheck()
  }





  removeProjectDdStatusDate(id:string, item:any){
    let form = this.getItemForm(item)

    const pastDatesArray = form?.get('pastDates') as FormArray;

    const index = pastDatesArray.controls.findIndex(control => control.get('id')?.value === id);

    if (index !== -1) {
      pastDatesArray.removeAt(index);
    }
    this.ddService.deleteProjectDueDiligenceDate(id).subscribe((response:any) => {
      this.openSnackBar('Successfully removed.', 'save-success')
    })
  }

  openSnackBar(message: string, panelClass:string) {
    const config = new MatSnackBarConfig();
    config.panelClass = [panelClass];
    config.horizontalPosition = "center";
    config.verticalPosition = "top";
    config.duration = 3000;
    this._snackbar.open(message, 'Ok', config);
  }



}
